import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Alert } from './ui/alert';
import Modal from './ui/modal';
import { Label } from './ui/label';
import {
  Trash2,
  Eye,
  Edit,
  Plus,
  Search,
  Loader2,
  UserCheck,
  Save,
} from 'lucide-react';
import { adminService } from '../services/admin';
import { User } from '../services/auth';

interface CreateUserForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: 'user' | 'admin' | 'super-admin';
}

interface EditUserForm {
  firstName: string;
  lastName: string;
  email: string;
  role: 'user' | 'admin' | 'super-admin';
}

const LocalUserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [editForm, setEditForm] = useState<EditUserForm>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'user',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter(
      user =>
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await adminService.getLocalUsers();
      setUsers(data);
      setFilteredUsers(data);
    } catch (err: any) {
      if (err.response?.status === 403 || err.response?.status === 401) {
        setError(
          'Access Denied. Super Admin privileges required to manage team members.'
        );
      } else {
        setError(err.response?.data?.message || 'Failed to fetch team members');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleEditUser = (user: User) => {
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email,
      role: user.role,
    });
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleCreateUser = async () => {
    try {
      setSaving(true);
      await adminService.createLocalUser(createForm);
      setShowCreateModal(false);
      setCreateForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'user',
      });
      await fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      setSaving(true);
      await adminService.updateLocalUser(selectedUser.id, editForm);
      setShowEditModal(false);
      setSelectedUser(null);
      await fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (
      window.confirm(
        'Are you sure you want to delete this user? This action cannot be undone.'
      )
    ) {
      try {
        await adminService.deleteUser(userId);
        setUsers(users.filter(user => user.id !== userId));
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const getBadgeColor = (status: string, type: 'role' | 'verified') => {
    if (type === 'role') {
      switch (status) {
        case 'admin':
          return 'bg-blue-100 text-blue-800';
        case 'super-admin':
          return 'bg-purple-100 text-purple-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    }
    return status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  return (
    <div className='space-y-4 sm:space-y-6'>
      <div className='flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-bold text-gray-900'>
            Manage Admin Access
          </h1>
          <p className='text-sm sm:text-base text-gray-600'>
            Create, edit and manage admin accounts
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className='flex items-center gap-2'
        >
          <Plus className='w-4 h-4' />
          Create Admin Account
        </Button>
      </div>

      {error && <Alert variant='destructive'>{error}</Alert>}

      <Card>
        <CardHeader className='p-4 sm:p-6'>
          <CardTitle className='flex items-center gap-2 text-lg sm:text-xl'>
            <UserCheck className='w-4 h-4 sm:w-5 sm:h-5' />
            Admin Accounts ({filteredUsers.length})
          </CardTitle>
          <div className='flex flex-col sm:flex-row sm:items-center gap-4 mt-4'>
            <div className='relative flex-1'>
              <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
              <Input
                placeholder='Search by name or email...'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className='pl-10'
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className='p-4 sm:p-6'>
          {filteredUsers.length === 0 ? (
            <div className='text-center py-8'>
              <UserCheck className='mx-auto h-12 w-12 text-gray-400' />
              {/* <h3 className='mt-2 text-sm font-medium text-gray-900'>
                No admin accounts
              </h3> */}
              <p className='mt-1 text-sm text-gray-500'>
                {searchTerm
                  ? 'No accounts match your search.'
                  : 'Get started by creating a new admin account.'}
              </p>
              {!searchTerm && (
                <div className='mt-6'>
                  <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className='w-4 h-4 mr-2' />
                    Create Admin Account
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className='space-y-3 sm:space-y-4'>
              {filteredUsers.map(user => (
                <div
                  key={user.id}
                  className='flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border rounded-lg gap-3 sm:gap-4'
                >
                  <div className='flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0'>
                    <div className='w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0'>
                      <span className='text-xs sm:text-sm font-medium text-gray-600'>
                        {user.firstName?.[0]}
                        {user.lastName?.[0]}
                      </span>
                    </div>

                    <div className='flex-1 min-w-0'>
                      <div className='flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-2 sm:mb-1'>
                        <h3 className='font-medium text-gray-900 text-sm sm:text-base truncate'>
                          {user.firstName || ''} {user.lastName || ''}
                        </h3>
                        <div className='flex flex-wrap gap-1'>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${getBadgeColor(user.role, 'role')}`}
                          >
                            {user.role.charAt(0).toUpperCase() +
                              user.role.slice(1)}
                          </span>
                          {user.emailVerified && (
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${getBadgeColor('verified', 'verified')}`}
                            >
                              Verified
                            </span>
                          )}
                        </div>
                      </div>

                      <div className='text-xs sm:text-sm text-gray-600 space-y-1'>
                        <p className='truncate'>{user.email}</p>
                        {user.company && (
                          <p className='hidden sm:block truncate'>
                            Company: {user.company}
                          </p>
                        )}
                        <p className='hidden sm:block'>
                          Created:{' '}
                          {new Date(user.createdAt).toLocaleDateString()}
                        </p>
                        <p className='sm:hidden'>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className='flex items-center gap-2 flex-shrink-0'>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => handleViewUser(user)}
                      className='flex items-center gap-1 text-xs sm:text-sm h-8 sm:h-9'
                    >
                      <Eye className='w-3 h-3 sm:w-4 sm:h-4' />
                      <span className='hidden sm:inline'>View</span>
                    </Button>
                    <Button
                      size='sm'
                      variant='outline'
                      onClick={() => handleEditUser(user)}
                      disabled={user.role === 'super-admin'}
                      className='flex items-center gap-1 text-xs sm:text-sm h-8 sm:h-9'
                      title={
                        user.role === 'super-admin'
                          ? 'Super-admin users cannot be edited'
                          : ''
                      }
                    >
                      <Edit className='w-3 h-3 sm:w-4 sm:h-4' />
                      <span className='hidden sm:inline'>Edit</span>
                    </Button>
                    {user.role !== 'super-admin' && (
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={() => handleDeleteUser(user.id)}
                        className='flex items-center gap-1 text-xs sm:text-sm h-8 sm:h-9 text-red-600 hover:text-red-700 hover:bg-red-50'
                      >
                        <Trash2 className='w-3 h-3 sm:w-4 sm:h-4' />
                        <span className='hidden sm:inline'>Delete</span>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View User Modal */}
      <Modal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        title='Account Details'
        footer={
          <Button variant='outline' onClick={() => setShowUserModal(false)}>
            Close
          </Button>
        }
      >
        {selectedUser && (
          <div className='grid gap-4 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label className='text-right'>Name:</Label>
              <div className='col-span-3'>
                {selectedUser.firstName || ''} {selectedUser.lastName || ''}
              </div>
            </div>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label className='text-right'>Email:</Label>
              <div className='col-span-3'>{selectedUser.email}</div>
            </div>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label className='text-right'>Role:</Label>
              <div className='col-span-3'>{selectedUser.role}</div>
            </div>
            {selectedUser.phone && (
              <div className='grid grid-cols-4 items-center gap-4'>
                <Label className='text-right'>Phone:</Label>
                <div className='col-span-3'>{selectedUser.phone}</div>
              </div>
            )}
            {selectedUser.company && (
              <div className='grid grid-cols-4 items-center gap-4'>
                <Label className='text-right'>Company:</Label>
                <div className='col-span-3'>{selectedUser.company}</div>
              </div>
            )}
            {selectedUser.department && (
              <div className='grid grid-cols-4 items-center gap-4'>
                <Label className='text-right'>Department:</Label>
                <div className='col-span-3'>{selectedUser.department}</div>
              </div>
            )}
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label className='text-right'>Created:</Label>
              <div className='col-span-3'>
                {new Date(selectedUser.createdAt).toLocaleString()}
              </div>
            </div>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label className='text-right'>Status:</Label>
            </div>
          </div>
        )}
      </Modal>

      {/* Create User Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title='Create New Admin Account'
        footer={
          <>
            <Button variant='outline' onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              disabled={
                saving ||
                !createForm.firstName ||
                !createForm.lastName ||
                !createForm.email ||
                !createForm.password
              }
            >
              {saving ? (
                <Loader2 className='w-4 h-4 animate-spin mr-2' />
              ) : (
                <Save className='w-4 h-4 mr-2' />
              )}
              Create Account
            </Button>
          </>
        }
      >
        <div className='grid gap-4 py-4'>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='firstName' className='text-right'>
              First Name
            </Label>
            <Input
              id='firstName'
              value={createForm.firstName}
              onChange={e =>
                setCreateForm({ ...createForm, firstName: e.target.value })
              }
              className='col-span-3'
              required
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='lastName' className='text-right'>
              Last Name
            </Label>
            <Input
              id='lastName'
              value={createForm.lastName}
              onChange={e =>
                setCreateForm({ ...createForm, lastName: e.target.value })
              }
              className='col-span-3'
              required
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='email' className='text-right'>
              Email
            </Label>
            <Input
              id='email'
              type='email'
              value={createForm.email}
              onChange={e =>
                setCreateForm({ ...createForm, email: e.target.value })
              }
              className='col-span-3'
              required
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='password' className='text-right'>
              Password
            </Label>
            <Input
              id='password'
              type='password'
              value={createForm.password}
              onChange={e =>
                setCreateForm({ ...createForm, password: e.target.value })
              }
              className='col-span-3'
              required
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='role' className='text-right'>
              Role
            </Label>
            <select
              id='role'
              value={createForm.role}
              onChange={e =>
                setCreateForm({
                  ...createForm,
                  role: e.target.value as 'user' | 'admin' | 'super-admin',
                })
              }
              className='col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
            >
              {/* <option value='user'>User</option> */}
              <option value='admin'>Admin</option>
              <option value='super-admin'>Super Admin</option>
            </select>
          </div>
        </div>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title='Edit User'
        footer={
          <>
            <Button variant='outline' onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateUser}
              disabled={
                saving ||
                !editForm.firstName ||
                !editForm.lastName ||
                !editForm.email
              }
            >
              {saving ? (
                <Loader2 className='w-4 h-4 animate-spin mr-2' />
              ) : (
                <Save className='w-4 h-4 mr-2' />
              )}
              Save Changes
            </Button>
          </>
        }
      >
        <div className='grid gap-4 py-4'>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='editFirstName' className='text-right'>
              First Name
            </Label>
            <Input
              id='editFirstName'
              value={editForm.firstName}
              onChange={e =>
                setEditForm({ ...editForm, firstName: e.target.value })
              }
              className='col-span-3'
              required
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='editLastName' className='text-right'>
              Last Name
            </Label>
            <Input
              id='editLastName'
              value={editForm.lastName}
              onChange={e =>
                setEditForm({ ...editForm, lastName: e.target.value })
              }
              className='col-span-3'
              required
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='editEmail' className='text-right'>
              Email
            </Label>
            <Input
              id='editEmail'
              type='email'
              value={editForm.email}
              onChange={e =>
                setEditForm({ ...editForm, email: e.target.value })
              }
              className='col-span-3'
              required
            />
          </div>
          <div className='grid grid-cols-4 items-center gap-4'>
            <Label htmlFor='editRole' className='text-right'>
              Role
            </Label>
            <select
              id='editRole'
              value={editForm.role}
              onChange={e =>
                setEditForm({
                  ...editForm,
                  role: e.target.value as 'user' | 'admin' | 'super-admin',
                })
              }
              disabled={selectedUser?.role === 'super-admin'}
              className='col-span-3 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
            >
              <option value='user'>User</option>
              <option value='admin'>Admin</option>
              <option value='super-admin'>Super Admin</option>
            </select>
            {selectedUser?.role === 'super-admin' && (
              <div className='col-span-4 text-sm text-muted-foreground text-center'>
                Super-admin role cannot be changed
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LocalUserManagement;
